/**
 * Casa del Bacalao · "¿Para cuántas personas?" en la ficha de producto.
 *
 * <casa-calculadora> lee las variantes por peso que imprime Liquid en
 * <script type="application/json" data-casa-calculadora-datos> y, al mover el número de personas,
 * recomienda la cantidad y la combinación de presentaciones reales disponibles.
 *
 * Reglas:
 *   - Todo en enteros: peso en gramos, dinero en centavos. El peso nunca se redondea a la baja.
 *   - Objetivo = personas × gramos por persona (rendimiento de los kits de la tienda: 200 g).
 *   - Combinación: la de MENOR peso total que alcanza el objetivo (con 1 kg y 500 g equivale a
 *     subir al siguiente múltiplo de 500 g); a igual peso, la de menos piezas; a igual piezas, la
 *     más barata. Solo variantes con available == true.
 *   - "Agregar" manda TODAS las líneas en una sola petición a /cart/add.js y avisa al carrito
 *     de Horizon igual que product-form.js: CartLinesUpdateEvent con promesa que se resuelve con
 *     el carrito y las secciones re-dibujadas (así el drawer se actualiza y se abre solo).
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const MIN_PERSONAS = 2;
const MAX_PERSONAS = 40;

/* <combinar> — lógica pura (sin DOM); las pruebas la extraen de aquí */

/** @param {number} a @param {number} b */
function mcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return a;
}

/**
 * @typedef {{ id: number, titulo?: string, precio: number, disponible: boolean, gramos: number }} Variante
 * @typedef {{ variante: Variante, cantidad: number }} Linea
 * @typedef {{ lineas: Linea[], gramos: number, precio: number, piezas: number }} Combinacion
 */

/**
 * Menor peso total ≥ objetivo con las presentaciones disponibles; desempata por piezas y precio.
 * @param {Variante[]} variantes
 * @param {number} objetivo - gramos (entero)
 * @returns {Combinacion | null}
 */
function combinar(variantes, objetivo) {
  /** @type {Map<number, Variante>} una por tamaño: la más barata */
  const porTamano = new Map();
  for (const v of variantes || []) {
    if (!v || v.disponible !== true) continue;
    if (!Number.isInteger(v.gramos) || v.gramos <= 0) continue;
    if (!Number.isInteger(v.precio) || v.precio < 0) continue;
    const previa = porTamano.get(v.gramos);
    if (!previa || v.precio < previa.precio) porTamano.set(v.gramos, v);
  }
  const opciones = [...porTamano.values()].sort((a, b) => b.gramos - a.gramos);
  if (!opciones.length || !Number.isInteger(objetivo) || objetivo <= 0) return null;

  // Trabajamos en "unidades" = MCD de los tamaños (100 g con 1 kg / 500 g / 400 g).
  const unidad = opciones.reduce((g, v) => mcd(g, v.gramos), 0);
  const meta = Math.ceil(objetivo / unidad);
  const tope = meta + opciones[0].gramos / unidad; // una combinación óptima nunca pasa de aquí

  /** @type {({ piezas: number, precio: number, ultima: number } | null)[]} */
  const mejor = new Array(tope + 1).fill(null);
  mejor[0] = { piezas: 0, precio: 0, ultima: -1 };

  for (let s = 1; s <= tope; s++) {
    for (let i = 0; i < opciones.length; i++) {
      const u = opciones[i].gramos / unidad;
      if (u > s || !mejor[s - u]) continue;
      const base = /** @type {{ piezas: number, precio: number }} */ (mejor[s - u]);
      const piezas = base.piezas + 1;
      const precio = base.precio + opciones[i].precio;
      const actual = mejor[s];
      if (!actual || piezas < actual.piezas || (piezas === actual.piezas && precio < actual.precio)) {
        mejor[s] = { piezas, precio, ultima: i };
      }
    }
  }

  let total = -1;
  for (let s = meta; s <= tope; s++) {
    if (mejor[s]) {
      total = s;
      break;
    }
  }
  if (total < 0) return null;

  const cuenta = new Array(opciones.length).fill(0);
  for (let s = total; s > 0; ) {
    const paso = /** @type {{ ultima: number }} */ (mejor[s]);
    cuenta[paso.ultima] += 1;
    s -= opciones[paso.ultima].gramos / unidad;
  }

  /** @type {Linea[]} la presentación grande primero */
  const lineas = [];
  opciones.forEach((variante, i) => {
    if (cuenta[i] > 0) lineas.push({ variante, cantidad: cuenta[i] });
  });

  const final = /** @type {{ piezas: number, precio: number }} */ (mejor[total]);
  return { lineas, gramos: total * unidad, precio: final.precio, piezas: final.piezas };
}

/**
 * "500 g", "1 kg", "1.5 kg", "1.25 kg". Solo enteros: nunca redondea.
 * @param {number} gramos
 */
function formatoPeso(gramos) {
  if (gramos < 1000) return `${gramos} g`;
  const kilos = Math.floor(gramos / 1000);
  const resto = gramos % 1000;
  if (!resto) return `${kilos} kg`;
  return `${kilos}.${String(resto).padStart(3, '0').replace(/0+$/, '')} kg`;
}

/**
 * "$1,428" · "$1,428.50" (decimales solo si hay centavos).
 * @param {number} centavos
 */
function formatoDinero(centavos) {
  const decimales = centavos % 100 ? 2 : 0;
  return (
    '$' +
    (centavos / 100).toLocaleString('es-MX', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })
  );
}

/**
 * "1 kg + 500 g" · "2 × 1 kg" · "2 × 1 kg + 500 g"
 * @param {Linea[]} lineas
 */
function formatoLineas(lineas) {
  return lineas
    .map(({ variante, cantidad }) => (cantidad > 1 ? `${cantidad} × ` : '') + formatoPeso(variante.gramos))
    .join(' + ');
}

/* </combinar> */

/**
 * Sustituye [clave] por nodos (texto o <strong>/<span> con casa-num) sin usar innerHTML.
 * @param {HTMLElement} destino
 * @param {string} plantilla
 * @param {Record<string, { texto: string, fuerte?: boolean }>} valores
 */
function pintar(destino, plantilla, valores) {
  const partes = plantilla.split(/(\[[a-z_]+\])/);
  const nodos = partes.map((parte) => {
    const clave = parte.slice(1, -1);
    const valor = parte.startsWith('[') && parte.endsWith(']') ? valores[clave] : undefined;
    if (!valor) return document.createTextNode(parte);
    const el = document.createElement(valor.fuerte ? 'strong' : 'span');
    el.className = 'casa-num';
    el.textContent = valor.texto;
    return el;
  });
  destino.replaceChildren(...nodos);
}

/** @param {string} plantilla @param {Record<string, string>} valores */
function sustituir(plantilla, valores) {
  return plantilla.replace(/\[([a-z_]+)\]/g, (m, clave) => (clave in valores ? valores[clave] : m));
}

class CasaCalculadora extends HTMLElement {
  /** @type {{ gramosPorPersona: number, variantes: Variante[], textos: Record<string, string>, kitsUrl: string, kitsDesde: number }} */
  datos = { gramosPorPersona: 200, variantes: [], textos: {}, kitsUrl: '', kitsDesde: 4 };

  /** @type {Combinacion | null} */
  combinacion = null;

  enVuelo = false;

  connectedCallback() {
    if (this.dataset.listo) return;
    const nodo = this.querySelector('script[data-casa-calculadora-datos]');
    try {
      this.datos = { ...this.datos, ...JSON.parse(nodo?.textContent || '{}') };
    } catch {
      return; // sin datos no se inventa nada: el bloque se queda como texto
    }
    this.dataset.listo = 'true';

    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-personas]'));
    this.menos = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-menos]'));
    this.mas = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mas]'));
    this.resultado = /** @type {HTMLElement | null} */ (this.querySelector('[data-resultado]'));
    this.combo = /** @type {HTMLElement | null} */ (this.querySelector('[data-combinacion]'));
    this.boton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-agregar]'));
    this.botonTexto = /** @type {HTMLElement | null} */ (this.querySelector('[data-agregar-texto]'));
    this.aviso = /** @type {HTMLElement | null} */ (this.querySelector('[data-aviso]'));
    this.kits = /** @type {HTMLElement | null} */ (this.querySelector('[data-kits]'));

    this.menos?.addEventListener('click', () => this.mover(-1));
    this.mas?.addEventListener('click', () => this.mover(1));
    this.input?.addEventListener('input', this.alEscribir);
    this.input?.addEventListener('change', this.alConfirmar);
    this.input?.addEventListener('blur', this.alConfirmar);
    this.input?.addEventListener('keydown', this.alTeclear);
    this.boton?.addEventListener('click', this.agregar);

    this.actualizar(this.personas());
  }

  /** @returns {number} */
  personas() {
    const n = parseInt((this.input?.value || '').replace(/\D/g, ''), 10);
    return Number.isInteger(n) ? n : NaN;
  }

  /** @param {number} n */
  limitar(n) {
    if (!Number.isInteger(n)) return MIN_PERSONAS;
    return Math.min(MAX_PERSONAS, Math.max(MIN_PERSONAS, n));
  }

  /** @param {number} delta */
  mover(delta) {
    const n = this.limitar((Number.isInteger(this.personas()) ? this.personas() : MIN_PERSONAS) + delta);
    if (this.input) this.input.value = String(n);
    this.actualizar(n);
  }

  alEscribir = () => {
    const n = this.personas();
    // Mientras escribe, solo se recalcula con un número válido; al salir del campo se corrige.
    if (Number.isInteger(n) && n >= MIN_PERSONAS && n <= MAX_PERSONAS) this.actualizar(n);
  };

  alConfirmar = () => {
    const n = this.limitar(this.personas());
    if (this.input && this.input.value !== String(n)) this.input.value = String(n);
    this.actualizar(n);
  };

  /** @param {KeyboardEvent} e */
  alTeclear = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.mover(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.mover(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this.alConfirmar();
    }
  };

  /** @param {number} personas */
  actualizar(personas) {
    const { gramosPorPersona, variantes, textos } = this.datos;
    const objetivo = personas * gramosPorPersona;
    this.combinacion = combinar(variantes, objetivo);

    if (this.menos) this.menos.disabled = personas <= MIN_PERSONAS;
    if (this.mas) this.mas.disabled = personas >= MAX_PERSONAS;

    const c = this.combinacion;
    // Sin combinación posible se dice el peso exacto (sin redondear a la baja) y no hay botón.
    const peso = formatoPeso(c ? c.gramos : objetivo);

    if (this.resultado) {
      pintar(this.resultado, textos.resultado || 'Para [personas] personas te recomendamos [peso]', {
        personas: { texto: String(personas) },
        peso: { texto: peso, fuerte: true },
      });
    }

    if (this.combo) {
      if (c) {
        this.combo.textContent = `${formatoLineas(c.lineas)} · ${formatoDinero(c.precio)}`;
      } else {
        this.combo.textContent = textos.sin_combinacion || '';
      }
    }

    if (this.boton) {
      this.boton.hidden = !c;
      this.boton.disabled = !c || this.enVuelo;
      if (this.botonTexto && !this.enVuelo) {
        this.botonTexto.textContent = sustituir(textos.boton || 'Agregar [peso] al carrito', { peso });
      }
    }

    if (this.kits) this.kits.hidden = !this.datos.kitsUrl || personas < this.datos.kitsDesde;
    if (this.aviso) this.aviso.textContent = '';
  }

  agregar = async () => {
    const c = this.combinacion;
    if (!c || this.enVuelo || !this.boton) return;
    const { textos } = this.datos;
    const peso = formatoPeso(c.gramos);

    this.enVuelo = true;
    this.boton.disabled = true;
    this.boton.setAttribute('aria-busy', 'true');
    if (this.botonTexto) this.botonTexto.textContent = textos.agregando || 'Agregando…';
    if (this.aviso) this.aviso.textContent = '';

    const items = c.lineas.map(({ variante, cantidad }) => ({ id: variante.id, quantity: cantidad }));

    // Mismas secciones que pide product-form.js: cada cart-items-component (drawer o página).
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const promesa = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: promesa.promise,
      })
    );

    const cuerpo = { items };
    if (sectionIds.length) {
      Object.assign(cuerpo, { sections: sectionIds.join(','), sections_url: window.location.pathname });
    }
    const rutas = /** @type {any} */ (window).Theme?.routes || {};

    let ok = false;
    try {
      const respuesta = await fetch(rutas.cart_add_url || '/cart/add.js', {
        ...fetchConfig('json', { body: JSON.stringify(cuerpo) }),
        credentials: 'same-origin',
      });
      const json = await respuesta.json();

      if (json.status || !respuesta.ok) {
        const mensaje = json.description || json.message || textos.error || '';
        this.dispatchEvent(
          new CartErrorEvent({
            error: json.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: json.description, errors: json.errors },
          })
        );
        const carrito = await this.leerCarrito().catch(() => null);
        if (carrito) {
          promesa.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
            detail: { didError: true, items: carrito.items, source: 'casa-calculadora', sourceId: this.id },
          });
        } else {
          promesa.reject(new Error(mensaje));
        }
        if (this.aviso) this.aviso.textContent = mensaje;
      } else {
        const carrito = await this.leerCarrito();
        promesa.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
          detail: {
            items: carrito.items,
            source: 'casa-calculadora',
            sourceId: this.id,
            itemCount: c.piezas,
            productId: this.dataset.productId,
            sections: json.sections,
            didError: false,
          },
        });
        ok = true;
        if (this.aviso) this.aviso.textContent = sustituir(textos.agregado || 'Agregamos [peso] a tu carrito.', { peso });
      }
    } catch (error) {
      console.error(error);
      promesa.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      if (this.aviso) this.aviso.textContent = textos.error || '';
    } finally {
      this.enVuelo = false;
      this.boton.removeAttribute('aria-busy');
      this.boton.disabled = false;
      if (this.botonTexto) {
        this.botonTexto.textContent = ok
          ? textos.agregado_boton || 'Agregado'
          : sustituir(textos.boton || 'Agregar [peso] al carrito', { peso });
      }
      if (ok) {
        window.setTimeout(() => {
          if (!this.enVuelo) this.actualizar(this.limitar(this.personas()));
        }, 2500);
      }
    }
  };

  /** Mismo camino que product-form.js#refreshCart */
  async leerCarrito() {
    const items = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (items) {
      await customElements.whenDefined('cart-items-component');
      if (typeof items.fetchCartData === 'function') return items.fetchCartData();
    }
    const rutas = /** @type {any} */ (window).Theme?.routes || {};
    const r = await fetch(`${rutas.cart_url || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!r.ok) throw new Error(`Failed to fetch cart: ${r.status}`);
    return r.json();
  }
}

if (!customElements.get('casa-calculadora')) customElements.define('casa-calculadora', CasaCalculadora);
